import axios from 'axios';
import { BASE_URL, API_URLS, rewriteLocalMediaInText, rewriteLocalMediaUrl } from '../constants/config';
import { checkIfTokenExpired, getTokensFromStore } from './tokens';
import { refreshTokens } from '../actions/usersActions';
import { getApiErrorMessage, getApiErrorPayload } from './stadiumsApi';
import { clampTimezoneOffset, getLoginTimezoneOffset } from './timezoneOffset';

/** Offset for social PNG/HTML: same as Date.getTimezoneOffset (Moscow ≈ −180). */
const resolvePictureTimezoneOffset = (value) => {
  const fromForm = clampTimezoneOffset(value);
  if (fromForm != null) return fromForm;
  return getLoginTimezoneOffset();
};

export const SOLID_UNDERLOZHKA_KEYS = ['blue', 'green', 'brown', 'yellow', 'red', 'purple'];

export const PATTERN_UNDERLOZHKA_KEYS = [
  'afl_long',
  'blue_long',
  'green_long',
  'brown_long',
  'yellow_long',
  'red_long',
  'purple_long',
  'nbsl_long',
  'spb_long',
  'superleague_long',
];

export const FALLBACK_SOLID_UNDERLOZHKI = [
  {
    key: 'blue',
    label: 'Синяя',
    type: 'solid',
    bgMode: 'preset',
    backgroundColor: '#2A3A8F',
    accent: '#1550F5',
    tableBackground: 'rgba(3, 12, 38, 0.48)',
    leagueBarBackground: '#081437',
  },
  {
    key: 'green',
    label: 'Зелёная',
    type: 'solid',
    bgMode: 'preset',
    backgroundColor: '#329C34',
    accent: '#5AD65C',
    tableBackground: 'rgba(4, 28, 18, 0.48)',
    leagueBarBackground: '#062014',
  },
  {
    key: 'brown',
    label: 'Коричневая',
    type: 'solid',
    bgMode: 'preset',
    backgroundColor: '#C95826',
    accent: '#F0A060',
    tableBackground: 'rgba(28, 18, 8, 0.48)',
    leagueBarBackground: '#2A1608',
  },
  {
    key: 'yellow',
    label: 'Жёлтая',
    type: 'solid',
    bgMode: 'preset',
    backgroundColor: '#CAB528',
    accent: '#F5E060',
    tableBackground: 'rgba(28, 24, 6, 0.48)',
    leagueBarBackground: '#2A2208',
  },
  {
    key: 'red',
    label: 'Красная',
    type: 'solid',
    bgMode: 'preset',
    backgroundColor: '#CB2438',
    accent: '#FF5A6A',
    tableBackground: 'rgba(28, 8, 12, 0.48)',
    leagueBarBackground: '#2A0A10',
  },
  {
    key: 'purple',
    label: 'Фиолетовая',
    type: 'solid',
    bgMode: 'preset',
    backgroundColor: '#581FB5',
    accent: '#9B5CFF',
    tableBackground: 'rgba(16, 8, 28, 0.48)',
    leagueBarBackground: '#160A2A',
  },
];

const PATTERN_META = {
  afl_long: { label: 'AFL', palette: 'blue' },
  blue_long: { label: 'Blue', palette: 'blue' },
  green_long: { label: 'Green', palette: 'green' },
  brown_long: { label: 'Brown', palette: 'brown' },
  yellow_long: { label: 'Yellow', palette: 'yellow' },
  red_long: { label: 'Red', palette: 'red' },
  purple_long: { label: 'Purple', palette: 'purple' },
  nbsl_long: { label: 'NBSL', palette: 'blue' },
  spb_long: { label: 'SPB', palette: 'blue' },
  superleague_long: { label: 'Superleague', palette: 'blue' },
};

const solidByKey = (key) => (
  FALLBACK_SOLID_UNDERLOZHKI.find((item) => item.key === key) || FALLBACK_SOLID_UNDERLOZHKI[0]
);

export const FALLBACK_PATTERN_UNDERLOZHKI = PATTERN_UNDERLOZHKA_KEYS.map((key) => {
  const meta = PATTERN_META[key] || { label: key, palette: 'blue' };
  const palette = solidByKey(meta.palette);
  return {
    key,
    label: meta.label,
    type: 'pattern',
    bgMode: 'cover',
    backgroundColor: palette.backgroundColor,
    accent: palette.accent,
    tableBackground: palette.tableBackground,
    leagueBarBackground: palette.leagueBarBackground,
  };
});

/** @deprecated use FALLBACK_SOLID_UNDERLOZHKI */
export const FALLBACK_UNDERLOZHKI = FALLBACK_SOLID_UNDERLOZHKI;

const MAX_UPLOAD_BYTES = 750 * 1024;
const ACCEPTED_UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

const trimOrEmpty = (value) => {
  const text = String(value || '').trim();
  return text || '';
};

const inferType = (key, explicitType) => {
  const type = String(explicitType || '').trim().toLowerCase();
  if (type === 'solid' || type === 'pattern') return type;
  if (SOLID_UNDERLOZHKA_KEYS.includes(key)) return 'solid';
  if (key.endsWith('_long') || PATTERN_UNDERLOZHKA_KEYS.includes(key)) return 'pattern';
  return 'solid';
};

const fallbackForKey = (key, type) => {
  if (type === 'pattern') {
    return FALLBACK_PATTERN_UNDERLOZHKI.find((item) => item.key === key)
      || FALLBACK_PATTERN_UNDERLOZHKI[0]
      || FALLBACK_SOLID_UNDERLOZHKI[0];
  }
  return solidByKey(key);
};

const cleanUnderlozhkaLabel = (value, fallback = '') => {
  const text = String(value || '')
    .replace(/\s*\(old\)\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text || fallback;
};

const rewriteShortBackground = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return rewriteLocalMediaUrl(value) || null;
  if (typeof value !== 'object') return value;
  const next = { ...value };
  ['url', 'src', 'preview', 'background', 'image', 'href', 'path'].forEach((key) => {
    if (typeof next[key] === 'string') next[key] = rewriteLocalMediaUrl(next[key]);
  });
  return next;
};

const normalizeUnderlozhka = (item, index = 0) => {
  if (!item || typeof item !== 'object') return null;
  const key = String(item.key || item.theme || item.id || '').trim();
  if (!key) return null;
  const type = inferType(key.toLowerCase(), item.type);
  const fallback = fallbackForKey(key.toLowerCase(), type);
  return {
    key,
    label: cleanUnderlozhkaLabel(item.label, fallback.label || key),
    type,
    bgMode: item.bgMode || (type === 'pattern' ? 'cover' : 'preset'),
    preview: rewriteLocalMediaUrl(item.preview || item.preview_url || item.sq || ''),
    background: rewriteLocalMediaUrl(item.background || item.background_url || item.url || ''),
    shortBackground: rewriteShortBackground(item.shortBackground),
    backgroundColor: item.backgroundColor || item.bgColor || fallback.backgroundColor,
    accent: item.accent || item.color || fallback.accent,
    tableBackground: item.tableBackground || fallback.tableBackground,
    leagueBarBackground: item.leagueBarBackground || fallback.leagueBarBackground,
    sort: index,
  };
};

const mapUnderlozhkaList = (list) => {
  if (!Array.isArray(list)) return [];
  const mapped = list
    .map((item, index) => normalizeUnderlozhka(item, index))
    .filter(Boolean);
  const seen = new Set();
  return mapped.filter((item) => {
    if (seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  });
};

const withEmptyMedia = (items) => items.map((item, index) => ({
  ...item,
  preview: item.preview || '',
  background: item.background || '',
  shortBackground: item.shortBackground || null,
  sort: index,
}));

export const createFallbackCatalog = (error = null) => {
  const solid = withEmptyMedia(FALLBACK_SOLID_UNDERLOZHKI);
  const pattern = withEmptyMedia(FALLBACK_PATTERN_UNDERLOZHKI);
  return {
    solid,
    pattern,
    all: [...solid, ...pattern],
    error,
  };
};

export const extractUnderlozhkiCatalog = (payload) => {
  const groups = payload?.groups && typeof payload.groups === 'object' ? payload.groups : {};
  let solid = mapUnderlozhkaList(groups.solid);
  let pattern = mapUnderlozhkaList(groups.pattern);

  if (!solid.length && !pattern.length) {
    const flat = mapUnderlozhkaList(
      Array.isArray(payload?.underlozhki)
        ? payload.underlozhki
        : Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : []
    );
    solid = flat.filter((item) => item.type === 'solid');
    pattern = flat.filter((item) => item.type === 'pattern');
    if (!solid.length && !pattern.length && flat.length) {
      solid = flat;
    }
  }

  if (!solid.length && !pattern.length) {
    return createFallbackCatalog();
  }

  if (!solid.length) solid = withEmptyMedia(FALLBACK_SOLID_UNDERLOZHKI);
  // pattern can be empty if API intentionally returned only solid; keep what we have

  return {
    solid,
    pattern,
    all: [...solid, ...pattern],
    error: null,
  };
};

/** Flat list helper for older callers. */
export const extractUnderlozhki = (payload) => extractUnderlozhkiCatalog(payload).all;

export const DEFAULT_TABLE_TITLE = 'ТАБЛИЦА';
export const DEFAULT_TOP10_TITLE = 'ТОП 10';
export const DEFAULT_CARDS_TITLE = 'КАРТОЧКИ';
export const DEFAULT_DISQUALIFICATIONS_TITLE = 'ДИСКВАЛИФИКАЦИИ';
export const DEFAULT_AFISHA_TITLE = '';
export const DEFAULT_SCHEDULE_TITLE = 'РАСПИСАНИЕ';
export const DEFAULT_RESULTS_TITLE = 'РЕЗУЛЬТАТЫ';
export const DEFAULT_CALENDAR_TITLE = 'КАЛЕНДАРЬ';
export const DEFAULT_PLAYOFF_TITLE = 'СЕТКА';
export const DEFAULT_PREVIEW_TITLE = '';
export const DEFAULT_DETAILED_RESULT_TITLE = 'РЕЗУЛЬТАТ МАТЧА';
export const DEFAULT_ANNOUNCEMENT_TITLE = '';
export const DEFAULT_QUOTE_NAME = '';
export const DEFAULT_QUOTE_TEXT = '';
export const DEFAULT_ACHIEVEMENTS_TITLE = 'MVP';
export const DEFAULT_MATCHDAY_TITLE = 'MATCHDAY';
export const DEFAULT_BROADCAST_PREVIEW_TITLE = 'ПРЯМАЯ ТРАНСЛЯЦИЯ';
export const DEFAULT_TOUR_TEAM_TITLE = 'СБОРНАЯ ТУРА';
export const DEFAULT_MATCH_STATS_TITLE = 'СТАТИСТИКА МАТЧА';
export const DEFAULT_MVP_TITLE = 'MVP ТУРА';
export const DEFAULT_MATCH_SUBS_TITLE = 'СОСТАВЫ';
export const DEFAULT_MATCH_LINEUPS_TITLE = 'СОСТАВЫ';
export const DEFAULT_FOOTER_COLOR = '#FFFFFF';
export const DEFAULT_FOOTER = {
  vk: { enabled: true, text: '/aflru' },
  web: { enabled: true, text: 'afl.ru' },
  instagram: { enabled: true, text: '/afl_russia' },
};

export const PICTURE_TYPE_TABLE = 'tournament_table_v2';
export const PICTURE_TYPE_TOP10 = 'top10_v2';
export const PICTURE_TYPE_CARDS = 'cards_v2';
export const PICTURE_TYPE_DISQUALIFICATIONS = 'disqualifications_v2';
export const PICTURE_TYPE_AFISHA = 'afisha_v2';
export const PICTURE_TYPE_SCHEDULE = 'schedule_v2';
export const PICTURE_TYPE_RESULTS = 'results_v2';
export const PICTURE_TYPE_CHESS = 'chess_v2';
export const PICTURE_TYPE_GROUP_TABLES = 'group_tables_v2';
export const PICTURE_TYPE_CALENDAR = 'calendar_v2';
export const PICTURE_TYPE_PLAYOFF = 'playoff_v2';
export const PICTURE_TYPE_PREVIEW = 'preview_v2';
export const PICTURE_TYPE_DETAILED_RESULT = 'detailed_result_v2';
export const PICTURE_TYPE_ANNOUNCEMENT = 'announcement_v2';
export const PICTURE_TYPE_QUOTE = 'quote_v2';
export const PICTURE_TYPE_ACHIEVEMENTS = 'achievements_v2';
export const PICTURE_TYPE_MATCH_COVER_GOAL = 'matchday_cover_v2';
/** Legacy admin/API key — alias of matchday_cover_v2 (render still POST /match_cover_goal). */
export const PICTURE_TYPE_MATCH_COVER_GOAL_LEGACY = 'match_cover_goal';
export const PICTURE_TYPE_BROADCAST_PREVIEW = 'broadcast_preview_v2';
export const PICTURE_TYPE_TOUR_TEAM = 'tour_team_v2';
export const PICTURE_TYPE_MATCH_STATS = 'match_stats_v2';
export const PICTURE_TYPE_MVP = 'mvp_v2';
export const PICTURE_TYPE_MATCH_SUBS = 'match_subs_v2';
export const PICTURE_TYPE_MATCH_LINEUPS = 'match_lineups_v2';

/** Common position codes for match_subs / match_lineups UI (code → short label). */
export const MATCH_SUBS_POSITION_OPTIONS = [
  { value: 'GK', name: 'ВРТ' },
  { value: 'LB', name: 'ЛЗ' },
  { value: 'CB', name: 'ЦЗ' },
  { value: 'RB', name: 'ПЗ' },
  { value: 'LM', name: 'ЛП' },
  { value: 'CM', name: 'ЦП' },
  { value: 'RM', name: 'ПП' },
  { value: 'LF', name: 'ЛН' },
  { value: 'ST', name: 'НАП' },
  { value: 'RF', name: 'ПН' },
  { value: 'UN', name: 'УН' },
];

/** Pitch slots for match_lineups_v2 (lineup_position_id). */
export const MATCH_LINEUP_SLOT_OPTIONS = [
  { value: 'fwd_0', name: 'НАП L' },
  { value: 'fwd_1', name: 'НАП C' },
  { value: 'fwd_2', name: 'НАП R' },
  { value: 'mid_0', name: 'ПЗ L' },
  { value: 'mid_1', name: 'ПЗ C' },
  { value: 'mid_2', name: 'ПЗ R' },
  { value: 'def_0', name: 'ЗАЩ L' },
  { value: 'def_1', name: 'ЗАЩ C' },
  { value: 'def_2', name: 'ЗАЩ R' },
  { value: 'gk', name: 'ВРТ' },
];

/** Normalize one side list for POST match_subs / match_lineups starters|bench. */
export const normalizeMatchSubsSideForApi = (players) => {
  if (!Array.isArray(players)) return [];
  return players
    .map((raw) => {
      if (!raw || typeof raw !== 'object') return null;
      const family = trimOrEmpty(raw.family);
      const name = trimOrEmpty(raw.name);
      const number = trimOrEmpty(raw.player_number || raw.match_number || raw.number);
      const position = trimOrEmpty(raw.position || raw.position_short);
      const lineupPositionId = trimOrEmpty(
        raw.lineup_position_id || raw.lineupPositionId || raw.slot
      );
      const playerId = Number(raw.player_id || raw.id);
      if (
        !family
        && !name
        && !number
        && !position
        && !lineupPositionId
        && !(Number.isFinite(playerId) && playerId > 0)
      ) {
        return null;
      }
      const item = {};
      if (family) item.family = family;
      if (name) item.name = name;
      if (number) item.player_number = number;
      if (position) item.position = position;
      if (lineupPositionId) item.lineup_position_id = lineupPositionId;
      if (Number.isFinite(playerId) && playerId > 0) item.player_id = playerId;
      return item;
    })
    .filter(Boolean);
};

/** Build positionOverrides map from editor rows / override state. */
export const buildMatchSubsPositionOverrides = (source) => {
  const result = {};
  const pushRow = (row) => {
    if (!row || typeof row !== 'object') return;
    const playerId = Number(row.player_id || row.id);
    const position = trimOrEmpty(row.position || row.position_short);
    if (!Number.isFinite(playerId) || playerId <= 0 || !position) return;
    result[String(playerId)] = position;
  };
  if (Array.isArray(source)) {
    source.forEach(pushRow);
    return result;
  }
  if (source && typeof source === 'object') {
    Object.keys(source).forEach((key) => {
      const value = source[key];
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        pushRow({ player_id: key, ...value });
        return;
      }
      const position = trimOrEmpty(value);
      const playerId = Number(key);
      if (Number.isFinite(playerId) && playerId > 0 && position) {
        result[String(playerId)] = position;
      }
    });
  }
  return result;
};

/** Build starters/bench payload from editor state { home, guest }. */
export const buildMatchSubsRosterPayload = (roster) => {
  const source = roster && typeof roster === 'object' ? roster : {};
  const startersSrc = source.starters && typeof source.starters === 'object' ? source.starters : {};
  const benchSrc = source.bench && typeof source.bench === 'object' ? source.bench : {};
  return {
    starters: {
      home: normalizeMatchSubsSideForApi(startersSrc.home),
      guest: normalizeMatchSubsSideForApi(startersSrc.guest),
    },
    bench: {
      home: normalizeMatchSubsSideForApi(benchSrc.home),
      guest: normalizeMatchSubsSideForApi(benchSrc.guest),
    },
  };
};

/** Map API roster side → editor rows. */
export const mapMatchSubsSideFromApi = (list) => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((raw, index) => ({
    key: `api-${raw?.player_id || index}-${index}`,
    player_id: Number(raw?.player_id || raw?.id) || undefined,
    player_number: String(raw?.number || raw?.player_number || raw?.match_number || '').trim(),
    family: String(raw?.family || '').trim(),
    name: String(raw?.name || '').trim(),
    position: String(raw?.position_short || raw?.position || '').trim(),
    lineup_position_id: String(
      raw?.lineup_position_id || raw?.lineupPositionId || raw?.slot || ''
    ).trim(),
  }));
};

export const mapMatchSubsRosterFromApi = (roster) => {
  const source = roster && typeof roster === 'object' ? roster : {};
  const starters = source.starters && typeof source.starters === 'object' ? source.starters : {};
  const bench = source.bench && typeof source.bench === 'object' ? source.bench : {};
  return {
    starters: {
      home: mapMatchSubsSideFromApi(starters.home || starters.home_team || starters.left),
      guest: mapMatchSubsSideFromApi(starters.guest || starters.guest_team || starters.right),
    },
    bench: {
      home: mapMatchSubsSideFromApi(bench.home || bench.home_team || bench.left),
      guest: mapMatchSubsSideFromApi(bench.guest || bench.guest_team || bench.right),
    },
  };
};

/** Match-preview side photo slots (canvas 1080×1080). */
export const PREVIEW_CANVAS_SIZE = 1080;
export const QUOTE_CANVAS_SIZE = 1080;
export const ACHIEVEMENTS_CANVAS_SIZE = 1080;
export const MVP_CANVAS_SIZE = 1080;
export const MATCH_COVER_CANVAS_WIDTH = 1920;
export const MATCH_COVER_CANVAS_HEIGHT = 1080;
/** Tour team of the round (1080×1350). */
export const TOUR_TEAM_CANVAS_WIDTH = 1080;
export const TOUR_TEAM_CANVAS_HEIGHT = 1350;
export const PREVIEW_PHOTO_SLOT = { width: 300, height: 1080 };
export const PREVIEW_PHOTO_ASPECT = PREVIEW_PHOTO_SLOT.width / PREVIEW_PHOTO_SLOT.height;

/** Formation schemes for tour_team_v2 freeform (API scheme index). */
export const TOUR_TEAM_SCHEME_OPTIONS = [
  { value: 0, name: '3-3-1' },
  { value: 1, name: '3-2-2' },
  { value: 2, name: '3-1-2' },
  { value: 3, name: '2-1-2' },
  { value: 4, name: '2-2' },
  { value: 6, name: '4-2-2' },
  { value: 7, name: '4-3-3' },
  { value: 8, name: '2-4-1' },
  { value: 9, name: '2-3-2' },
  { value: 10, name: '2-2-3' },
  { value: 11, name: '1-2' },
];

/** Known social-picture API error codes → i18n keys. */
export const SOCIAL_PICTURE_ERROR_I18N = {
  league_type_not_groups: 'igErrLeagueTypeNotGroups',
  league_type_not_playoff: 'igErrLeagueTypeNotPlayoff',
  league_settings_missing: 'igErrLeagueSettingsMissing',
  no_groups: 'igErrNoGroups',
  no_playoff_rounds: 'igErrNoPlayoffRounds',
  groups_not_found: 'igErrGroupsNotFound',
  missing_league_or_season: 'igErrMissingLeagueOrSeason',
  missing_league_season_or_date: 'igErrMissingLeagueSeasonOrDate',
  missing_match_id: 'igErrMissingMatchId',
  missing_league_or_match: 'igErrMissingLeagueOrMatch',
  missing_team_id: 'igErrMissingTeamId',
  missing_player_id: 'igErrMissingPlayerId',
  missing_tour_team_or_players: 'igErrMissingTourTeamOrPlayers',
  match_not_found: 'igErrMatchNotFound',
  player_not_found: 'igErrPlayerNotFound',
  team_not_found: 'igErrTeamNotFound',
  invalid_date: 'igErrInvalidDate',
  unknown_stage_ids: 'igErrUnknownStageIds',
  stages_not_contiguous: 'igErrStagesNotContiguous',
  third_requires_final: 'igErrThirdRequiresFinal',
  unknown_team_ids: 'igErrUnknownTeamIds',
  no_matches_for_teams: 'igErrNoMatchesForTeams',
  unknown_track_keys: 'igErrUnknownTrackKeys',
  empty_track_selection: 'igErrEmptyTrackSelection',
};

export const formatSocialPictureErrorPayload = (payload, t, fallback = '') => {
  const data = payload && typeof payload === 'object' ? payload : null;
  const code = String(data?.code || '').trim();
  const serverErr = typeof data?.err === 'string' ? data.err.trim() : '';
  const leagueType = data?.league_type != null ? data.league_type : data?.leagueType;

  if (code && typeof t === 'function') {
    const i18nKey = SOCIAL_PICTURE_ERROR_I18N[code];
    if (i18nKey) {
      const translated = t(i18nKey, {
        code,
        leagueType: leagueType != null ? String(leagueType) : '',
      });
      if (translated && translated !== i18nKey) return translated;
    }
  }

  if (serverErr) return serverErr;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
  if (typeof data?.error === 'string' && data.error.trim()) return data.error.trim();
  return fallback;
};

export const getSocialPictureErrorMessage = (error, t, fallback = '') => {
  const payload = getApiErrorPayload(error) || error?.socialPayload || null;
  const fromPayload = formatSocialPictureErrorPayload(payload, t, '');
  if (fromPayload) return fromPayload;
  return getApiErrorMessage(error, fallback);
};

export const isMatchListPictureType = (pictureType) => (
  pictureType === PICTURE_TYPE_SCHEDULE || pictureType === PICTURE_TYPE_RESULTS
);

/** match_subs_v2 + match_lineups_v2 share roster / overrides contract. */
export const isMatchRosterPictureType = (pictureType) => (
  pictureType === PICTURE_TYPE_MATCH_SUBS
  || pictureType === PICTURE_TYPE_MATCH_LINEUPS
);

/** Single-match pictures: pick match_id (preview, detailed result, …). */
export const isSingleMatchPictureType = (pictureType) => (
  pictureType === PICTURE_TYPE_PREVIEW
  || pictureType === PICTURE_TYPE_DETAILED_RESULT
  || pictureType === PICTURE_TYPE_MATCH_COVER_GOAL
  || pictureType === PICTURE_TYPE_BROADCAST_PREVIEW
  || pictureType === PICTURE_TYPE_MATCH_STATS
  || isMatchRosterPictureType(pictureType)
);

export const isBroadcastPreviewPictureType = (pictureType) => (
  pictureType === PICTURE_TYPE_BROADCAST_PREVIEW
);

export const isMatchCoverGoalPictureType = (pictureType) => (
  pictureType === PICTURE_TYPE_MATCH_COVER_GOAL
  || pictureType === PICTURE_TYPE_MATCH_COVER_GOAL_LEGACY
);

export const isTourTeamPictureType = (pictureType) => (
  pictureType === PICTURE_TYPE_TOUR_TEAM
);

/** Normalize lineup slots for POST /tour_team_v2 freeform players[]. */
export const normalizeTourTeamPlayersForApi = (players) => {
  if (!Array.isArray(players)) return [];
  return players
    .map((raw) => {
      if (!raw || typeof raw !== 'object') return null;
      const playerId = Number(raw.player_id || raw.id);
      if (!Number.isFinite(playerId) || playerId <= 0) return null;
      const indexRaw = raw.index != null ? raw.index : (raw.position || raw.position_id);
      let index;
      if (typeof indexRaw === 'string' && indexRaw.trim()) {
        index = indexRaw.trim().replace(/-/g, '_');
      }
      const item = { player_id: playerId };
      if (index) item.index = index;
      const family = trimOrEmpty(raw.family);
      const name = trimOrEmpty(raw.name);
      const image = trimOrEmpty(raw.image || raw.player_image);
      const teamImage = trimOrEmpty(raw.team_image || raw.team_logo);
      const teamName = trimOrEmpty(raw.team_name);
      const teamId = Number(raw.team_id);
      if (family) item.family = family;
      if (name) item.name = name;
      if (image) item.image = image;
      if (teamImage) item.team_image = teamImage;
      if (teamName) item.team_name = teamName;
      if (Number.isFinite(teamId) && teamId > 0) item.team_id = teamId;
      if (raw.star === true || raw.star === 1 || raw.star === '1') item.star = true;
      return item;
    })
    .filter(Boolean);
};

export const resolveTourTeamId = (item) => {
  if (!item || typeof item !== 'object') return null;
  const id = Number(item.tour_team_id || item.tourTeamId || item.id);
  return Number.isFinite(id) && id > 0 ? id : null;
};

export const resolveTourTeamSchemeIndex = (tourTeam) => {
  if (!tourTeam) return 0;
  if (Number.isInteger(tourTeam.scheme)) {
    return tourTeam.scheme >= 0 ? tourTeam.scheme : 0;
  }
  if (Number.isInteger(tourTeam.scheme_id) && !Array.isArray(tourTeam.scheme_id)) {
    return tourTeam.scheme_id >= 0 ? tourTeam.scheme_id : 0;
  }
  return 0;
};

/** Normalize playoff cup/track from isHtml availableTracks. */
export const normalizePlayoffAvailableTrack = (item = {}) => {
  const key = String(item.key || item.track_key || item.trackKey || '').trim();
  if (!key) return null;
  const stageIds = (Array.isArray(item.stageIds) ? item.stageIds : (Array.isArray(item.stage_ids) ? item.stage_ids : []))
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
  const stagesRaw = Number(item.stages);
  const matchesRaw = Number(item.matches);
  return {
    key,
    title: String(item.title || item.track_title || item.trackTitle || key),
    stageIds,
    stages: Number.isFinite(stagesRaw) && stagesRaw >= 0 ? stagesRaw : stageIds.length,
    matches: Number.isFinite(matchesRaw) && matchesRaw >= 0 ? matchesRaw : 0,
  };
};

export const mergePlayoffAvailableTracks = (previous = [], incoming = []) => {
  const byKey = new Map();
  (Array.isArray(previous) ? previous : []).forEach((item) => {
    const track = normalizePlayoffAvailableTrack(item);
    if (track) byKey.set(track.key, track);
  });
  (Array.isArray(incoming) ? incoming : []).forEach((item) => {
    const track = normalizePlayoffAvailableTrack(item);
    if (track) byKey.set(track.key, track);
  });
  return Array.from(byKey.values());
};

/** Normalize playoff stage from isHtml availableStages.
 *  slots/size = phase capacity; matches = filled count (may be 0). Never drop matches === 0.
 */
export const normalizePlayoffAvailableStage = (item = {}) => {
  const stageId = Number(item.stage_id || item.stageId || item.id);
  if (!Number.isFinite(stageId) || stageId <= 0) return null;
  const sizeRaw = Number(item.size);
  const slotsRaw = Number(item.slots);
  const matchesRaw = Number(item.matches);
  // Capacity only — do not fall back to matches (0 would wipe capacity).
  const capacity = (Number.isFinite(sizeRaw) && sizeRaw > 0)
    ? sizeRaw
    : ((Number.isFinite(slotsRaw) && slotsRaw > 0) ? slotsRaw : 0);
  const trackKey = String(item.track_key || item.trackKey || '').trim();
  return {
    stage_id: stageId,
    title: String(item.title || item.name || stageId),
    size: capacity,
    slots: capacity,
    matches: Number.isFinite(matchesRaw) && matchesRaw >= 0 ? matchesRaw : 0,
    is_final: Boolean(item.is_final || item.isFinal),
    is_third_place: Boolean(item.is_third_place || item.isThirdPlace),
    track_key: trackKey,
    track_title: String(item.track_title || item.trackTitle || trackKey || ''),
  };
};

/** Union stage catalogs so filtered previews do not hide empty rounds from the picker. */
export const mergePlayoffAvailableStages = (previous = [], incoming = []) => {
  const byId = new Map();
  (Array.isArray(previous) ? previous : []).forEach((item) => {
    const stage = normalizePlayoffAvailableStage(item);
    if (stage) byId.set(stage.stage_id, stage);
  });
  (Array.isArray(incoming) ? incoming : []).forEach((item) => {
    const stage = normalizePlayoffAvailableStage(item);
    if (stage) byId.set(stage.stage_id, stage);
  });
  return Array.from(byId.values()).sort((a, b) => {
    if (Boolean(a.is_third_place) !== Boolean(b.is_third_place)) {
      return a.is_third_place ? 1 : -1;
    }
    const sizeDiff = (Number(b.size) || 0) - (Number(a.size) || 0);
    if (sizeDiff) return sizeDiff;
    return a.stage_id - b.stage_id;
  });
};

export const normalizePlayoffAvailableTeam = (item = {}) => {
  const id = Number(item.id || item.team_id || item.teamId);
  if (!Number.isFinite(id) || id <= 0) return null;
  const name = String(item.name || item.short_name || item.shortName || id);
  return {
    id,
    name,
    short_name: String(item.short_name || item.shortName || ''),
    image: item.image || item.logo || '',
  };
};

/**
 * Playoff stage multi-select rules (client-side mirror of server):
 * - main chain must be contiguous within the same track_key (cup)
 * - third place alone OR with final (+ contiguous tail)
 */
export const isPlayoffStageSelectionValid = (selectedIds, availableStages = []) => {
  const ids = (Array.isArray(selectedIds) ? selectedIds : [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
  if (!ids.length) return true;

  const stages = (Array.isArray(availableStages) ? availableStages : [])
    .map(normalizePlayoffAvailableStage)
    .filter(Boolean);
  const byId = new Map(stages.map((item) => [item.stage_id, item]));
  const selected = ids.map((id) => byId.get(id)).filter(Boolean);
  if (selected.length !== ids.length) return false;

  const third = selected.filter((item) => item.is_third_place);
  const main = selected.filter((item) => !item.is_third_place);
  if (third.length && main.length && !main.some((item) => item.is_final)) {
    return false;
  }

  const mainByTrack = new Map();
  main.forEach((item) => {
    const key = item.track_key || '';
    if (!mainByTrack.has(key)) mainByTrack.set(key, []);
    mainByTrack.get(key).push(item);
  });

  for (const [trackKey, trackMain] of mainByTrack.entries()) {
    if (trackMain.length <= 1) continue;
    const mainOrder = stages.filter((item) => (
      !item.is_third_place && (item.track_key || '') === trackKey
    ));
    const indices = trackMain
      .map((item) => mainOrder.findIndex((entry) => entry.stage_id === item.stage_id))
      .filter((index) => index >= 0)
      .sort((a, b) => a - b);
    if (indices.length !== trackMain.length) return false;
    for (let i = 1; i < indices.length; i += 1) {
      if (indices[i] !== indices[i - 1] + 1) return false;
    }
  }
  return true;
};

export const playoffSelectionHasFinal = (selectedIds, availableStages = []) => {
  const ids = Array.isArray(selectedIds) ? selectedIds : [];
  if (!ids.length) return true; // empty = all stages (includes final if any)
  const stages = (Array.isArray(availableStages) ? availableStages : [])
    .map(normalizePlayoffAvailableStage)
    .filter(Boolean);
  const byId = new Map(stages.map((item) => [item.stage_id, item]));
  return ids.some((id) => byId.get(Number(id))?.is_final);
};

export const FALLBACK_V2_TYPES = [
  {
    key: PICTURE_TYPE_TABLE,
    label: 'Таблица',
    labelEn: 'Table',
    endpoint: '/social_pictures/tournament_table',
    bodyDefaults: { v2: true },
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_TOP10,
    label: 'Топ-10',
    labelEn: 'Top-10',
    endpoint: '/social_pictures/top10',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_CARDS,
    label: 'Карточки',
    labelEn: 'Cards',
    endpoint: '/social_pictures/cards',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_DISQUALIFICATIONS,
    label: 'Дисквалификации',
    labelEn: 'Disqualifications',
    endpoint: '/social_pictures/disqualifications',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_AFISHA,
    label: 'Афиша',
    labelEn: 'Poster',
    endpoint: '/social_pictures/afisha',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_SCHEDULE,
    label: 'Расписание',
    labelEn: 'Schedule',
    endpoint: '/social_pictures/schedule',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_RESULTS,
    label: 'Результаты',
    labelEn: 'Results',
    endpoint: '/social_pictures/results',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_CHESS,
    label: 'Шахматка',
    labelEn: 'Chess table',
    endpoint: '/social_pictures/chess',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_GROUP_TABLES,
    label: 'Таблицы групп',
    labelEn: 'Group tables',
    endpoint: '/social_pictures/group_tables',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_CALENDAR,
    label: 'Календарь',
    labelEn: 'Calendar',
    endpoint: '/social_pictures/calendar',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_PLAYOFF,
    label: 'Сетка',
    labelEn: 'Playoff bracket',
    endpoint: '/social_pictures/playoff',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_PREVIEW,
    label: 'Превью матча',
    labelEn: 'Match preview',
    endpoint: '/social_pictures/preview',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_DETAILED_RESULT,
    label: 'Подробный результат',
    labelEn: 'Detailed result',
    endpoint: '/social_pictures/detailed_result',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_ANNOUNCEMENT,
    label: 'Объявление',
    labelEn: 'Announcement',
    endpoint: '/social_pictures/announcement',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_QUOTE,
    label: 'Цитата',
    labelEn: 'Quote',
    endpoint: '/social_pictures/quote',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_ACHIEVEMENTS,
    label: 'Достижения',
    labelEn: 'Achievements',
    endpoint: '/social_pictures/achievements',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_MATCH_COVER_GOAL,
    label: 'Плашки на видео',
    labelEn: 'Video plates',
    endpoint: '/social_pictures/match_cover_goal',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: false, userRecent: true },
  },
  {
    key: PICTURE_TYPE_BROADCAST_PREVIEW,
    label: 'Превью трансляции',
    labelEn: 'Broadcast preview',
    endpoint: '/social_pictures/broadcast_preview',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_TOUR_TEAM,
    label: 'Сборная тура',
    labelEn: 'Team of the round',
    endpoint: '/social_pictures/tour_team_v2',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_MATCH_STATS,
    label: 'Статистика матча',
    labelEn: 'Match statistics',
    endpoint: '/social_pictures/match_stats_v2',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_MVP,
    label: 'MVP',
    labelEn: 'MVP',
    endpoint: '/social_pictures/mvp_v2',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_MATCH_SUBS,
    label: 'Составы',
    labelEn: 'Lineups',
    endpoint: '/social_pictures/match_subs_v2',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
  {
    key: PICTURE_TYPE_MATCH_LINEUPS,
    label: 'Составы на поле',
    labelEn: 'Lineups on pitch',
    endpoint: '/social_pictures/match_lineups_v2',
    bodyDefaults: {},
    supports: { underlozhki: true, styles: true, sponsors: true, userRecent: true },
  },
];

/** Types that may be missing from GET /v2_types but are available in admin. */
export const LOCAL_EXTRA_PICTURE_TYPES = [
  FALLBACK_V2_TYPES.find((item) => item.key === PICTURE_TYPE_MATCH_COVER_GOAL),
  FALLBACK_V2_TYPES.find((item) => item.key === PICTURE_TYPE_BROADCAST_PREVIEW),
  FALLBACK_V2_TYPES.find((item) => item.key === PICTURE_TYPE_TOUR_TEAM),
  FALLBACK_V2_TYPES.find((item) => item.key === PICTURE_TYPE_MATCH_STATS),
  FALLBACK_V2_TYPES.find((item) => item.key === PICTURE_TYPE_MVP),
  FALLBACK_V2_TYPES.find((item) => item.key === PICTURE_TYPE_MATCH_SUBS),
  FALLBACK_V2_TYPES.find((item) => item.key === PICTURE_TYPE_MATCH_LINEUPS),
].filter(Boolean);

export const mergePictureTypesCatalog = (remoteTypes) => {
  const list = Array.isArray(remoteTypes) && remoteTypes.length
    ? remoteTypes.map((item) => ({ ...item }))
    : FALLBACK_V2_TYPES.map((item) => ({ ...item }));
  const keys = new Set(list.map((item) => item.key));
  // Deduplicate legacy match_cover_goal if both keys somehow remain
  if (keys.has(PICTURE_TYPE_MATCH_COVER_GOAL) && keys.has(PICTURE_TYPE_MATCH_COVER_GOAL_LEGACY)) {
    const idx = list.findIndex((item) => item.key === PICTURE_TYPE_MATCH_COVER_GOAL_LEGACY);
    if (idx >= 0) list.splice(idx, 1);
    keys.delete(PICTURE_TYPE_MATCH_COVER_GOAL_LEGACY);
  }
  LOCAL_EXTRA_PICTURE_TYPES.forEach((item) => {
    if (!keys.has(item.key)) list.push({ ...item });
  });
  return list;
};

export const defaultTitleForPictureType = (pictureType) => {
  if (pictureType === PICTURE_TYPE_TOP10) return DEFAULT_TOP10_TITLE;
  if (pictureType === PICTURE_TYPE_CARDS) return DEFAULT_CARDS_TITLE;
  if (pictureType === PICTURE_TYPE_DISQUALIFICATIONS) return DEFAULT_DISQUALIFICATIONS_TITLE;
  if (pictureType === PICTURE_TYPE_AFISHA) return DEFAULT_AFISHA_TITLE;
  if (pictureType === PICTURE_TYPE_SCHEDULE) return DEFAULT_SCHEDULE_TITLE;
  if (pictureType === PICTURE_TYPE_RESULTS) return DEFAULT_RESULTS_TITLE;
  if (pictureType === PICTURE_TYPE_CALENDAR) return DEFAULT_CALENDAR_TITLE;
  if (pictureType === PICTURE_TYPE_PLAYOFF) return DEFAULT_PLAYOFF_TITLE;
  if (pictureType === PICTURE_TYPE_PREVIEW) return DEFAULT_PREVIEW_TITLE;
  if (pictureType === PICTURE_TYPE_DETAILED_RESULT) return DEFAULT_DETAILED_RESULT_TITLE;
  if (pictureType === PICTURE_TYPE_ANNOUNCEMENT) return DEFAULT_ANNOUNCEMENT_TITLE;
  if (pictureType === PICTURE_TYPE_QUOTE) return DEFAULT_QUOTE_NAME;
  if (pictureType === PICTURE_TYPE_ACHIEVEMENTS) return DEFAULT_ACHIEVEMENTS_TITLE;
  if (pictureType === PICTURE_TYPE_MATCH_COVER_GOAL) return DEFAULT_MATCHDAY_TITLE;
  if (pictureType === PICTURE_TYPE_BROADCAST_PREVIEW) return DEFAULT_BROADCAST_PREVIEW_TITLE;
  if (pictureType === PICTURE_TYPE_TOUR_TEAM) return DEFAULT_TOUR_TEAM_TITLE;
  if (pictureType === PICTURE_TYPE_MATCH_STATS) return DEFAULT_MATCH_STATS_TITLE;
  if (pictureType === PICTURE_TYPE_MVP) return DEFAULT_MVP_TITLE;
  if (pictureType === PICTURE_TYPE_MATCH_SUBS) return DEFAULT_MATCH_SUBS_TITLE;
  if (pictureType === PICTURE_TYPE_MATCH_LINEUPS) return DEFAULT_MATCH_LINEUPS_TITLE;
  // table + chess + group tables share default title «ТАБЛИЦА»
  return DEFAULT_TABLE_TITLE;
};

export const normalizeV2Type = (item = {}) => {
  let key = String(item.key || '').trim();
  if (!key) return null;
  if (key === PICTURE_TYPE_MATCH_COVER_GOAL_LEGACY) {
    key = PICTURE_TYPE_MATCH_COVER_GOAL;
  }
  return {
    key,
    label: String(item.label || key).trim(),
    labelEn: String(item.labelEn || item.label_en || item.label || key).trim(),
    endpoint: String(item.endpoint || '').trim()
      || (key === PICTURE_TYPE_MATCH_COVER_GOAL ? '/social_pictures/match_cover_goal' : '')
      || (key === PICTURE_TYPE_BROADCAST_PREVIEW ? '/social_pictures/broadcast_preview' : '')
      || (key === PICTURE_TYPE_TOUR_TEAM ? '/social_pictures/tour_team_v2' : '')
      || (key === PICTURE_TYPE_MATCH_STATS ? '/social_pictures/match_stats_v2' : '')
      || (key === PICTURE_TYPE_MVP ? '/social_pictures/mvp_v2' : '')
      || (key === PICTURE_TYPE_MATCH_SUBS ? '/social_pictures/match_subs_v2' : '')
      || (key === PICTURE_TYPE_MATCH_LINEUPS ? '/social_pictures/match_lineups_v2' : ''),
    bodyDefaults: item.bodyDefaults && typeof item.bodyDefaults === 'object' ? item.bodyDefaults : {},
    supports: item.supports && typeof item.supports === 'object' ? item.supports : {},
  };
};

export const extractV2Types = (payload) => {
  const list = Array.isArray(payload?.types)
    ? payload.types
    : (Array.isArray(payload) ? payload : []);
  const normalized = list.map(normalizeV2Type).filter(Boolean);
  return mergePictureTypesCatalog(
    normalized.length ? normalized : FALLBACK_V2_TYPES.map((item) => ({ ...item }))
  );
};

export const resolvePictureEndpoint = (pictureType, types = FALLBACK_V2_TYPES) => {
  const found = (types || []).find((item) => item.key === pictureType);
  if (found?.endpoint) {
    return found.endpoint.startsWith('/') ? found.endpoint : `/${found.endpoint}`;
  }
  if (pictureType === PICTURE_TYPE_TOP10) return API_URLS.socialTop10;
  if (pictureType === PICTURE_TYPE_CARDS) return API_URLS.socialCards;
  if (pictureType === PICTURE_TYPE_DISQUALIFICATIONS) return API_URLS.socialDisqualifications;
  if (pictureType === PICTURE_TYPE_AFISHA) return API_URLS.socialAfisha;
  if (pictureType === PICTURE_TYPE_SCHEDULE) return API_URLS.socialSchedule;
  if (pictureType === PICTURE_TYPE_RESULTS) return API_URLS.socialResults;
  if (pictureType === PICTURE_TYPE_CHESS) return API_URLS.socialChess;
  if (pictureType === PICTURE_TYPE_GROUP_TABLES) return API_URLS.socialGroupTables;
  if (pictureType === PICTURE_TYPE_CALENDAR) return API_URLS.socialCalendar;
  if (pictureType === PICTURE_TYPE_PLAYOFF) return API_URLS.socialPlayoff;
  if (pictureType === PICTURE_TYPE_PREVIEW) return API_URLS.socialPreview;
  if (pictureType === PICTURE_TYPE_DETAILED_RESULT) return API_URLS.socialDetailedResult;
  if (pictureType === PICTURE_TYPE_ANNOUNCEMENT) return API_URLS.socialAnnouncement;
  if (pictureType === PICTURE_TYPE_QUOTE) return API_URLS.socialQuote;
  if (pictureType === PICTURE_TYPE_ACHIEVEMENTS) return API_URLS.socialAchievements;
  if (pictureType === PICTURE_TYPE_MATCH_COVER_GOAL) return API_URLS.socialMatchCoverGoal;
  if (pictureType === PICTURE_TYPE_BROADCAST_PREVIEW) return API_URLS.socialBroadcastPreview;
  if (pictureType === PICTURE_TYPE_TOUR_TEAM) return API_URLS.socialTourTeam;
  if (pictureType === PICTURE_TYPE_MATCH_STATS) return API_URLS.socialMatchStats;
  if (pictureType === PICTURE_TYPE_MVP) return API_URLS.socialMvp;
  if (pictureType === PICTURE_TYPE_MATCH_SUBS) return API_URLS.socialMatchSubs;
  if (pictureType === PICTURE_TYPE_MATCH_LINEUPS) return API_URLS.socialMatchLineups;
  return API_URLS.socialTournamentTable;
};

const buildFooterPayload = (footer) => {
  if (!footer || typeof footer !== 'object') return null;
  const result = {};
  let hasChanges = false;

  ['vk', 'web', 'instagram'].forEach((key) => {
    const item = footer[key];
    if (!item || typeof item !== 'object') return;
    const entry = {};
    if (item.enabled === false) {
      entry.enabled = false;
      hasChanges = true;
    }
    const text = trimOrEmpty(item.text);
    if (text) {
      entry.text = text;
      hasChanges = true;
    }
    if (Object.keys(entry).length) {
      result[key] = entry;
    }
  });

  return hasChanges ? result : null;
};

export const buildCustomBg = ({
  theme = 'blue',
  url = '',
  backgroundColor = '',
  accent = '',
  tableBackground = '',
  leagueBarBackground = '',
  stadiumBackground = '',
  logoCaption = '',
  title = '',
  subtitle = '',
  titleColor = '',
  footerColor = '',
  footer = null,
} = {}) => {
  const customBg = { theme: String(theme || 'blue') };
  const nextUrl = trimOrEmpty(url);
  const nextBg = trimOrEmpty(backgroundColor);
  const nextAccent = trimOrEmpty(accent);
  const nextTable = trimOrEmpty(tableBackground);
  const nextBar = trimOrEmpty(leagueBarBackground);
  const nextStadium = trimOrEmpty(stadiumBackground);
  const nextCaption = trimOrEmpty(logoCaption);
  const nextTitle = trimOrEmpty(title);
  const nextSubtitle = trimOrEmpty(subtitle);
  const nextTitleColor = trimOrEmpty(titleColor);
  const nextFooterColor = trimOrEmpty(footerColor);
  const nextFooter = buildFooterPayload(footer);

  if (nextUrl) customBg.url = nextUrl;
  if (nextBg) customBg.backgroundColor = nextBg;
  if (nextAccent) customBg.accent = nextAccent;
  if (nextTable) customBg.tableBackground = nextTable;
  if (nextBar) customBg.leagueBarBackground = nextBar;
  if (nextStadium) customBg.stadiumBackground = nextStadium;
  if (nextCaption) customBg.logoCaption = nextCaption;
  if (nextTitle) customBg.title = nextTitle;
  if (nextSubtitle) customBg.subtitle = nextSubtitle;
  if (nextTitleColor) customBg.titleColor = nextTitleColor;
  if (nextFooterColor) customBg.footerColor = nextFooterColor;
  if (nextFooter) customBg.footer = nextFooter;

  return customBg;
};

export const buildSocialPictureBody = ({
  pictureType = PICTURE_TYPE_TABLE,
  leagueId,
  seasonId,
  stageId,
  dateFrom,
  dateTo,
  date,
  code,
  matchId,
  headline,
  tournamentTitle,
  year,
  text,
  teamId,
  personName,
  quote,
  achievementTitle,
  leftPhoto,
  rightPhoto,
  includeUpcoming,
  verticalLayout,
  groups,
  splitByGroups,
  groupStagesOnly,
  stageIds,
  teamIds,
  trackKeys,
  playerId,
  tourTeamId,
  homeBarColor,
  guestBarColor,
  matchSubsStarters,
  matchSubsBench,
  matchSubsUseDbDefaults,
  positionOverrides,
  overwritePositions,
  listOnly,
  activeMatches,
  assignedToMe,
  isHtml,
  customBg,
  saveLeagueStyle,
  sponsors,
  saveLeagueSponsors,
  columns,
  typesCatalog,
  timezoneOffset,
} = {}) => {
  const pictureTimezoneOffset = resolvePictureTimezoneOffset(timezoneOffset);
  if (pictureType === PICTURE_TYPE_MATCH_COVER_GOAL) {
    const body = {
      isHtml: Boolean(isHtml),
      customBg: buildCustomBg(customBg),
    };
    if (pictureTimezoneOffset != null) body.timezoneOffset = pictureTimezoneOffset;
    const league = Number(leagueId);
    if (Number.isFinite(league) && league > 0) body.leagueId = league;
    const mid = Number(matchId);
    if (Number.isFinite(mid) && mid > 0) body.match_id = mid;
    const stage = Number(stageId);
    if (Number.isFinite(stage) && stage > 0) body.stage_id = stage;
    // Top-level title replaces MATCHDAY; do not duplicate into customBg.title
    const matchday = trimOrEmpty(headline) || trimOrEmpty(achievementTitle) || trimOrEmpty(text)
      || trimOrEmpty(customBg?.title);
    if (matchday) body.title = matchday;
    if (body.customBg && body.customBg.title) {
      delete body.customBg.title;
    }
    if (!isHtml && saveLeagueStyle === false) {
      body.saveLeagueStyle = false;
    }
    return body;
  }
  const typeMeta = (typesCatalog || FALLBACK_V2_TYPES).find((item) => item.key === pictureType);
  const body = {
    isHtml: Boolean(isHtml),
    customBg: buildCustomBg(customBg),
    ...(typeMeta?.bodyDefaults && typeof typeMeta.bodyDefaults === 'object'
      ? typeMeta.bodyDefaults
      : {}),
  };
  if (pictureTimezoneOffset != null) body.timezoneOffset = pictureTimezoneOffset;
  const league = Number(leagueId);
  if (Number.isFinite(league) && league > 0) {
    body.leagueId = league;
  }
  if (isMatchListPictureType(pictureType)) {
    const stage = Number(stageId);
    const from = trimOrEmpty(dateFrom);
    const to = trimOrEmpty(dateTo);
    if (stage > 0) {
      body.stage_id = stage;
    } else if (from && to) {
      body.date_from = from;
      body.date_to = to;
      const season = Number(seasonId);
      if (season > 0) body.season_id = season;
    }
    if (pictureType === PICTURE_TYPE_RESULTS && includeUpcoming) {
      body.includeUpcoming = true;
    }
    // assigned_to_me — только матчи, где текущий user назначен в staff-слотах
    if (assignedToMe) {
      body.assigned_to_me = true;
    }
    // listOnly — только JSON со списком; activeMatches при listOnly сервер игнорирует.
    if (listOnly) {
      body.listOnly = true;
    } else if (Array.isArray(activeMatches)) {
      body.activeMatches = activeMatches
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    }
  } else if (!isSingleMatchPictureType(pictureType)) {
    body.season_id = Number(seasonId);
  }
  if (pictureType === PICTURE_TYPE_AFISHA) {
    const day = trimOrEmpty(date);
    if (day) body.date = day;
    const footerCode = trimOrEmpty(code);
    if (footerCode) body.code = footerCode;
  }
  if (pictureType === PICTURE_TYPE_PLAYOFF) {
    if (verticalLayout) body.verticalLayout = true;
    if (Array.isArray(trackKeys) && trackKeys.length) {
      const keys = trackKeys
        .map((key) => String(key || '').trim())
        .filter(Boolean);
      if (keys.length) body.trackKeys = keys;
    }
    if (Array.isArray(stageIds) && stageIds.length) {
      body.stageIds = stageIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    }
    if (Array.isArray(teamIds) && teamIds.length) {
      body.teamIds = teamIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    }
  }
  if (isSingleMatchPictureType(pictureType)) {
    const mid = Number(matchId);
    if (Number.isFinite(mid) && mid > 0) body.match_id = mid;
    const head = trimOrEmpty(headline);
    if (head) {
      if (
        pictureType === PICTURE_TYPE_DETAILED_RESULT
        || pictureType === PICTURE_TYPE_MATCH_STATS
        || pictureType === PICTURE_TYPE_BROADCAST_PREVIEW
        || isMatchRosterPictureType(pictureType)
      ) {
        body.title = head;
      } else {
        body.headline = head;
      }
    }
  }
  if (pictureType === PICTURE_TYPE_BROADCAST_PREVIEW) {
    const tourTitle = trimOrEmpty(tournamentTitle);
    if (tourTitle) body.tournamentTitle = tourTitle;
    const seasonYear = trimOrEmpty(year);
    if (seasonYear) body.year = seasonYear;
    if (body.customBg && body.customBg.title) {
      delete body.customBg.title;
    }
  }
  if (isMatchRosterPictureType(pictureType)) {
    const stage = Number(stageId);
    if (Number.isFinite(stage) && stage > 0) body.stage_id = stage;
    if (matchSubsUseDbDefaults === false) {
      body.useDbDefaults = false;
      const roster = buildMatchSubsRosterPayload({
        starters: matchSubsStarters,
        bench: matchSubsBench,
      });
      body.starters = roster.starters;
      body.bench = roster.bench;
    } else if (matchSubsStarters || matchSubsBench) {
      const roster = buildMatchSubsRosterPayload({
        starters: matchSubsStarters,
        bench: matchSubsBench,
      });
      const hasStarters = roster.starters.home.length || roster.starters.guest.length;
      const hasBench = roster.bench.home.length || roster.bench.guest.length;
      if (hasStarters) body.starters = roster.starters;
      if (hasBench) body.bench = roster.bench;
    }
    const overrides = buildMatchSubsPositionOverrides(positionOverrides);
    if (Object.keys(overrides).length) {
      body.positionOverrides = overrides;
    }
    if (overwritePositions === true) {
      body.overwritePositions = true;
    }
  }
  if (pictureType === PICTURE_TYPE_PREVIEW) {
    const left = trimOrEmpty(leftPhoto);
    if (left) body.leftPhoto = left;
    const right = trimOrEmpty(rightPhoto);
    if (right) body.rightPhoto = right;
  }
  if (pictureType === PICTURE_TYPE_ANNOUNCEMENT) {
    const announce = trimOrEmpty(text) || trimOrEmpty(headline);
    if (announce) body.text = announce;
    if (body.customBg && body.customBg.title) {
      delete body.customBg.title;
    }
  }
  if (pictureType === PICTURE_TYPE_QUOTE) {
    const tid = Number(teamId);
    if (Number.isFinite(tid) && tid > 0) body.team_id = tid;
    const author = trimOrEmpty(personName) || trimOrEmpty(headline);
    if (author) body.name = author;
    const quoteText = trimOrEmpty(quote) || trimOrEmpty(text);
    if (quoteText) body.quote = quoteText;
    if (body.customBg && body.customBg.title) {
      delete body.customBg.title;
    }
  }
  if (pictureType === PICTURE_TYPE_ACHIEVEMENTS) {
    const achTitle = trimOrEmpty(achievementTitle) || trimOrEmpty(headline) || trimOrEmpty(text);
    if (achTitle) body.title = achTitle;
    const achName = trimOrEmpty(personName);
    if (achName) body.name = achName;
    if (body.customBg && body.customBg.title) {
      delete body.customBg.title;
    }
  }
  if (pictureType === PICTURE_TYPE_TOUR_TEAM) {
    const tid = Number(tourTeamId);
    if (Number.isFinite(tid) && tid > 0) body.tour_team_id = tid;
  }
  if (pictureType === PICTURE_TYPE_MATCH_STATS) {
    const homeBar = trimOrEmpty(homeBarColor);
    const guestBar = trimOrEmpty(guestBarColor);
    if (homeBar) body.homeBarColor = homeBar;
    if (guestBar) body.guestBarColor = guestBar;
  }
  if (pictureType === PICTURE_TYPE_MVP) {
    const pid = Number(playerId);
    if (Number.isFinite(pid) && pid > 0) body.player_id = pid;
    const tid = Number(teamId);
    if (Number.isFinite(tid) && tid > 0) body.team_id = tid;
    const mvpTitle = trimOrEmpty(headline) || trimOrEmpty(achievementTitle) || trimOrEmpty(text);
    if (mvpTitle) body.title = mvpTitle;
    if (body.customBg && body.customBg.title) {
      delete body.customBg.title;
    }
  }
  if (pictureType === PICTURE_TYPE_GROUP_TABLES && Array.isArray(groups) && groups.length) {
    body.groups = groups
      .map((item) => {
        if (item && typeof item === 'object') {
          return trimOrEmpty(item.group_name || item.name || item.groupName);
        }
        return trimOrEmpty(item);
      })
      .filter(Boolean)
      .slice(0, 4);
  }
  if (pictureType === PICTURE_TYPE_TOP10 || pictureType === PICTURE_TYPE_CARDS) {
    if (typeof splitByGroups === 'boolean') {
      body.splitByGroups = splitByGroups;
    }
    if (splitByGroups !== false && Array.isArray(groups) && groups.length) {
      body.groups = groups
        .map((item) => {
          if (item && typeof item === 'object') {
            return trimOrEmpty(item.group_name || item.name || item.groupName);
          }
          return trimOrEmpty(item);
        })
        .filter(Boolean);
    }
  }
  if (pictureType === PICTURE_TYPE_CALENDAR) {
    const cols = Number(columns);
    if (Number.isFinite(cols) && cols >= 1 && cols <= 4) {
      body.columns = Math.round(cols);
    }
    if (groupStagesOnly) {
      body.groupStagesOnly = true;
    }
    if (Array.isArray(stageIds) && stageIds.length) {
      body.stageIds = stageIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    }
  } else if (pictureType !== PICTURE_TYPE_CALENDAR && Array.isArray(columns) && columns.length) {
    body.columns = columns;
  }
  if (pictureType === PICTURE_TYPE_TABLE && body.v2 == null) {
    body.v2 = true;
  }
  if (!isHtml && saveLeagueStyle === false) {
    body.saveLeagueStyle = false;
  }
  // Always send top-level sponsors when provided — including [] to clear league template after PNG.
  if (Array.isArray(sponsors)) {
    body.sponsors = sponsors;
  }
  if (!isHtml && saveLeagueSponsors === false) {
    body.saveLeagueSponsors = false;
  }
  return body;
};

/** @deprecated use buildSocialPictureBody */
export const buildTournamentTableBody = (form = {}) => (
  buildSocialPictureBody({ ...form, pictureType: form.pictureType || PICTURE_TYPE_TABLE })
);

export const emptyStylesCache = () => ({
  picture_type: 'tournament_table_v2',
  league_id: null,
  championat_id: null,
  league: null,
  leagueStyle: null,
  champSchemes: [],
  userRecent: [],
  note: '',
});

export const extractStylesCache = (payload) => {
  const data = payload && typeof payload === 'object' ? payload : {};
  return {
    picture_type: data.picture_type || 'tournament_table_v2',
    league_id: data.league_id || data.leagueId || null,
    championat_id: data.championat_id
      || data.league?.championat_id
      || data.championatId
      || null,
    league: data.league || null,
    leagueStyle: data.leagueStyle || null,
    champSchemes: Array.isArray(data.champSchemes) ? data.champSchemes : [],
    userRecent: Array.isArray(data.userRecent) ? data.userRecent : [],
    note: data.note || '',
  };
};

/** Map stored style → form fields (without custom upload url). */
export const styleToFormFields = (style) => {
  const source = style && typeof style === 'object' ? style : {};
  const footer = source.footer && typeof source.footer === 'object' ? source.footer : {};
  const vk = footer.vk && typeof footer.vk === 'object' ? footer.vk : {};
  const web = footer.web && typeof footer.web === 'object' ? footer.web : {};
  const ig = footer.instagram && typeof footer.instagram === 'object' ? footer.instagram : {};

  const enabledOr = (value, flat) => {
    if (typeof value === 'boolean') return value;
    if (typeof flat === 'boolean') return flat;
    return true;
  };

  return {
    theme: String(source.theme || source.underlozhka || source.preset || 'blue'),
    accent: trimOrEmpty(source.accent || source.color),
    tableBackground: trimOrEmpty(source.tableBackground),
    leagueBarBackground: trimOrEmpty(source.leagueBarBackground),
    stadiumBackground: trimOrEmpty(
      source.stadiumBackground || source.stadiumColor || source.ribbonColor || source.stadiumBg
    ),
    logoCaption: trimOrEmpty(source.logoCaption),
    title: trimOrEmpty(source.title || source.titleText),
    subtitle: trimOrEmpty(source.subtitle),
    titleColor: trimOrEmpty(source.titleColor),
    footerColor: trimOrEmpty(source.footerColor),
    footerVkEnabled: enabledOr(vk.enabled, source.footerVkEnabled),
    footerVkText: trimOrEmpty(vk.text || source.footerVkText),
    footerWebEnabled: enabledOr(web.enabled, source.footerWebEnabled),
    footerWebText: trimOrEmpty(web.text || source.footerWebText),
    footerIgEnabled: enabledOr(ig.enabled, source.footerInstagramEnabled),
    footerIgText: trimOrEmpty(ig.text || source.footerInstagramText),
  };
};

export const stylePreviewLabel = (style, fallback = '') => {
  const fields = styleToFormFields(style);
  const parts = [
    fields.theme,
    fields.title || fields.accent || fields.logoCaption,
  ].filter(Boolean);
  return parts.join(' · ') || fallback;
};

const postJson = async (dispatch, url, body = {}) => {
  const tokens = await ensureAuth(dispatch);
  const response = await axios.post(BASE_URL.url + url, body, {
    headers: authHeaders(tokens.access_token),
  });
  return response.data;
};

export const fetchTournamentTableStyles = async (dispatch, { leagueId, pictureType }) => {
  const body = { leagueId: Number(leagueId) };
  const styleType = pictureType === PICTURE_TYPE_MATCH_COVER_GOAL_LEGACY
    ? PICTURE_TYPE_MATCH_COVER_GOAL
    : pictureType;
  if (styleType) body.picture_type = styleType;
  const data = await postJson(dispatch, API_URLS.socialTournamentTableStyles, body);
  return extractStylesCache(data);
};

export const saveTournamentTableLeagueStyle = async (dispatch, { leagueId, customBg, pictureType }) => {
  const body = {
    leagueId: Number(leagueId),
    customBg: buildCustomBg(customBg),
  };
  const styleType = pictureType === PICTURE_TYPE_MATCH_COVER_GOAL_LEGACY
    ? PICTURE_TYPE_MATCH_COVER_GOAL
    : pictureType;
  if (styleType) body.picture_type = styleType;
  return postJson(dispatch, API_URLS.socialTournamentTableStylesSaveLeague, body);
};

export const saveTournamentTableScheme = async (dispatch, {
  championatId,
  leagueId,
  name,
  customBg,
  id = null,
}) => {
  const body = {
    name: String(name || '').trim(),
    customBg: buildCustomBg(customBg),
  };
  if (id) body.id = Number(id);
  if (championatId) body.championat_id = Number(championatId);
  if (leagueId) body.leagueId = Number(leagueId);
  return postJson(dispatch, API_URLS.socialTournamentTableStylesSchemesSave, body);
};

export const deleteTournamentTableScheme = async (dispatch, { id, championatId, leagueId }) => {
  const body = { id: Number(id) };
  if (championatId) body.championat_id = Number(championatId);
  if (leagueId) body.leagueId = Number(leagueId);
  return postJson(dispatch, API_URLS.socialTournamentTableStylesSchemesDelete, body);
};

const ensureAuth = async (dispatch) => {
  if (checkIfTokenExpired()) {
    await dispatch(refreshTokens());
  }
  const tokens = getTokensFromStore();
  if (!tokens?.access_token) {
    const error = new Error('Пользователь не авторизован');
    error.response = { status: 401, data: 'Пользователь не авторизован' };
    throw error;
  }
  return tokens;
};

const authHeaders = (accessToken, contentType = 'application/json') => ({
  Authorization: `Bearer ${accessToken}`,
  ...(contentType ? { 'Content-Type': contentType } : {}),
});

const parseBlobErrorPayload = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = (await data.text()).trim();
      if (!text) return null;
      try {
        const json = JSON.parse(text);
        return json && typeof json === 'object' ? json : { err: String(json || text) };
      } catch (parseError) {
        return { err: text };
      }
    } catch (readError) {
      return null;
    }
  }
  return getApiErrorPayload(error);
};

const parseBlobError = async (error, fallback, t = null) => {
  const payload = await parseBlobErrorPayload(error);
  if (payload) {
    const formatted = formatSocialPictureErrorPayload(payload, t, '');
    if (formatted) return { message: formatted, payload };
  }
  return { message: getApiErrorMessage(error, fallback), payload: null };
};

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const loadImageFromDataUrl = (dataUrl) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = dataUrl;
});

const canvasToDataUrl = (canvas, type, quality) => {
  try {
    return canvas.toDataURL(type, quality);
  } catch (error) {
    return canvas.toDataURL('image/png');
  }
};

/** Compress large uploads toward ~200–750KB before putting into JSON body. */
export const compressDataUrl = async (dataUrl, { maxBytes = MAX_UPLOAD_BYTES } = {}) => {
  if (!dataUrl || dataUrl.length <= maxBytes * 1.37) return dataUrl;

  const image = await loadImageFromDataUrl(dataUrl);
  const canvas = document.createElement('canvas');
  let width = image.naturalWidth || image.width;
  let height = image.naturalHeight || image.height;
  const maxSide = 1920;
  if (width > maxSide || height > maxSide) {
    const ratio = Math.min(maxSide / width, maxSide / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, 0, 0, width, height);

  let quality = 0.86;
  let result = canvasToDataUrl(canvas, 'image/jpeg', quality);
  while (result.length > maxBytes * 1.37 && quality > 0.45) {
    quality -= 0.1;
    result = canvasToDataUrl(canvas, 'image/jpeg', quality);
  }
  return result;
};

export const UNDERLOZHKA_CANVAS_WIDTH = 1080;
export const CHESS_CANVAS_WIDTH = 1440;
export const CALENDAR_CANVAS_WIDTH = 1620;
export const PLAYOFF_CANVAS_WIDTH = 1620;
export const DEFAULT_UNDERLOZHKA_ASPECT = UNDERLOZHKA_CANVAS_WIDTH / 1400;

export const getCanvasWidthForPictureType = (pictureType) => {
  if (pictureType === PICTURE_TYPE_CHESS) return CHESS_CANVAS_WIDTH;
  if (pictureType === PICTURE_TYPE_CALENDAR) return CALENDAR_CANVAS_WIDTH;
  if (pictureType === PICTURE_TYPE_PLAYOFF) return PLAYOFF_CANVAS_WIDTH;
  if (pictureType === PICTURE_TYPE_PREVIEW) return PREVIEW_CANVAS_SIZE;
  if (pictureType === PICTURE_TYPE_QUOTE) return QUOTE_CANVAS_SIZE;
  if (pictureType === PICTURE_TYPE_ACHIEVEMENTS) return ACHIEVEMENTS_CANVAS_SIZE;
  if (pictureType === PICTURE_TYPE_MVP) return MVP_CANVAS_SIZE;
  if (pictureType === PICTURE_TYPE_MATCH_COVER_GOAL) return MATCH_COVER_CANVAS_WIDTH;
  if (pictureType === PICTURE_TYPE_BROADCAST_PREVIEW) return MATCH_COVER_CANVAS_WIDTH;
  if (pictureType === PICTURE_TYPE_TOUR_TEAM) return TOUR_TEAM_CANVAS_WIDTH;
  return UNDERLOZHKA_CANVAS_WIDTH;
};

export const getUnderlozhkaAspect = (pageHeight, canvasWidth = UNDERLOZHKA_CANVAS_WIDTH) => {
  const height = Number(pageHeight);
  const width = Number(canvasWidth) > 0 ? Number(canvasWidth) : UNDERLOZHKA_CANVAS_WIDTH;
  if (!Number.isFinite(height) || height <= 0) return width / 1400;
  return width / height;
};

export const readImageFileAsDataUrl = async (file) => {
  if (!file) throw new Error('No file selected');
  const type = String(file.type || '').toLowerCase();
  if (type && !ACCEPTED_UPLOAD_TYPES.includes(type)) {
    throw new Error('Unsupported image type');
  }
  const dataUrl = await readFileAsDataUrl(file);
  if (!String(dataUrl).startsWith('data:image/')) {
    throw new Error('Invalid image data');
  }
  return dataUrl;
};

export const fileToUnderlozhkaDataUrl = async (file) => (
  compressDataUrl(await readImageFileAsDataUrl(file))
);

export const fetchUnderlozhkiCatalog = async (dispatch) => {
  const tokens = await ensureAuth(dispatch);
  try {
    const response = await axios.get(BASE_URL.url + API_URLS.socialTournamentTableUnderlozhki, {
      headers: authHeaders(tokens.access_token, null),
    });
    return extractUnderlozhkiCatalog(response.data);
  } catch (error) {
    return createFallbackCatalog(getApiErrorMessage(error, 'Failed to load underlozhki'));
  }
};

export const fetchV2PictureTypes = async (dispatch) => {
  const tokens = await ensureAuth(dispatch);
  try {
    const response = await axios.get(BASE_URL.url + API_URLS.socialV2Types, {
      headers: authHeaders(tokens.access_token, null),
    });
    return extractV2Types(response.data);
  } catch (error) {
    return mergePictureTypesCatalog(FALLBACK_V2_TYPES.map((item) => ({ ...item })));
  }
};

/** Подпись матча в пикере расписания / результатов. */
export const formatMatchListCandidateLabel = (match, { withCenter = false } = {}) => {
  if (!match || typeof match !== 'object') return '—';
  const home = match.home_team_name || match.home_team?.short_name || match.home_team?.name || '—';
  const guest = match.guest_team_name || match.guest_team?.short_name || match.guest_team?.name || '—';
  const date = String(match.date || '').slice(0, 10);
  const time = String(match.time || '').slice(0, 5);
  const kick = [date, time].filter(Boolean).join(' ');
  const teams = `${home} — ${guest}`;
  const center = withCenter ? String(match.center || '').trim() : '';
  if (kick && center) return `${kick} · ${teams} · ${center}`;
  if (kick) return `${kick} · ${teams}`;
  if (center) return `${teams} · ${center}`;
  return teams;
};

/**
 * Кандидаты для чекбоксов (schedule_v2 / results_v2).
 * POST … { listOnly: true } → matches[] (+ days, у results — center).
 */
export const fetchMatchListCandidates = async (dispatch, form) => {
  const tokens = await ensureAuth(dispatch);
  const pictureType = form.pictureType || PICTURE_TYPE_SCHEDULE;
  const endpoint = resolvePictureEndpoint(pictureType, form.typesCatalog);
  const body = buildSocialPictureBody({
    ...form,
    pictureType,
    isHtml: false,
    listOnly: true,
    activeMatches: undefined,
  });
  const response = await axios.post(BASE_URL.url + endpoint, body, {
    headers: authHeaders(tokens.access_token),
  });
  const payload = response?.data && typeof response.data === 'object' ? response.data : {};
  const matches = Array.isArray(payload.matches)
    ? payload.matches
      .map((item) => ({
        ...item,
        id: Number(item?.id ?? item?.match_id),
      }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0)
    : [];
  return {
    matches,
    days: Array.isArray(payload.days) ? payload.days : [],
    mode: payload.mode || null,
    filter: payload.filter || null,
  };
};

export const fetchSocialPictureHtml = async (dispatch, form) => {
  const tokens = await ensureAuth(dispatch);
  const pictureType = form.pictureType || PICTURE_TYPE_TABLE;
  const endpoint = resolvePictureEndpoint(pictureType, form.typesCatalog);
  const body = buildSocialPictureBody({ ...form, pictureType, isHtml: true });
  const response = await axios.post(BASE_URL.url + endpoint, body, {
    headers: authHeaders(tokens.access_token),
  });
  const html = response?.data?.html;
  if (typeof html !== 'string' || !html.trim()) {
    throw new Error('Empty preview HTML');
  }
  const fromApi = Number(response?.data?.canvasWidth);
  const fromHeight = Number(response?.data?.canvasHeight);
  const photoSlots = Array.isArray(response?.data?.photoSlots) ? response.data.photoSlots : null;
  const availableStages = Array.isArray(response?.data?.availableStages)
    ? response.data.availableStages
    : null;
  const availableTeams = Array.isArray(response?.data?.availableTeams)
    ? response.data.availableTeams
    : null;
  const availableTracks = Array.isArray(response?.data?.availableTracks)
    ? response.data.availableTracks
    : null;
  const selection = response?.data?.selection && typeof response.data.selection === 'object'
    ? response.data.selection
    : null;
  return {
    html: rewriteLocalMediaInText(html),
    canvasWidth: Number.isFinite(fromApi) && fromApi > 0
      ? fromApi
      : getCanvasWidthForPictureType(pictureType),
    canvasHeight: Number.isFinite(fromHeight) && fromHeight > 0
      ? fromHeight
      : (pictureType === PICTURE_TYPE_PREVIEW
        || pictureType === PICTURE_TYPE_QUOTE
        || pictureType === PICTURE_TYPE_ACHIEVEMENTS
        || pictureType === PICTURE_TYPE_MVP
        ? PREVIEW_CANVAS_SIZE
        : (pictureType === PICTURE_TYPE_MATCH_COVER_GOAL
          || pictureType === PICTURE_TYPE_BROADCAST_PREVIEW
          ? MATCH_COVER_CANVAS_HEIGHT
          : (pictureType === PICTURE_TYPE_TOUR_TEAM ? TOUR_TEAM_CANVAS_HEIGHT : undefined))),
    photoSlots,
    availableStages,
    availableTeams,
    availableTracks,
    multiTrack: Boolean(response?.data?.multiTrack),
    selection,
    verticalLayout: Boolean(response?.data?.verticalLayout),
    orientation: response?.data?.orientation || null,
    barColors: response?.data?.barColors && typeof response.data.barColors === 'object'
      ? response.data.barColors
      : null,
    match: response?.data?.match && typeof response.data.match === 'object'
      ? response.data.match
      : null,
    roster: response?.data?.roster && typeof response.data.roster === 'object'
      ? response.data.roster
      : null,
  };
};

const decodeBase64ToUint8Array = (raw) => {
  let text = String(raw || '').trim();
  if (!text) throw new Error('Empty ZIP response');
  // Strip optional JSON quotes / data-url prefix.
  if ((text.startsWith('"') && text.endsWith('"')) || (text.startsWith("'") && text.endsWith("'"))) {
    text = text.slice(1, -1);
  }
  const dataUrlMatch = text.match(/^data:[^;]+;base64,(.+)$/i);
  if (dataUrlMatch) text = dataUrlMatch[1];
  text = text.replace(/\s+/g, '');
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

/** Download match event plates pack (ZIP as base64 body). */
export const fetchMatchCoverGoalZipBlob = async (dispatch, form) => {
  const tokens = await ensureAuth(dispatch);
  const endpoint = resolvePictureEndpoint(PICTURE_TYPE_MATCH_COVER_GOAL, form.typesCatalog);
  const body = buildSocialPictureBody({
    ...form,
    pictureType: PICTURE_TYPE_MATCH_COVER_GOAL,
    isHtml: false,
  });
  try {
    const response = await axios.post(BASE_URL.url + endpoint, body, {
      headers: authHeaders(tokens.access_token),
      responseType: 'text',
      timeout: 180000,
      transformResponse: [(data) => data],
    });
    const contentType = String(response.headers?.['content-type'] || '').toLowerCase();
    const raw = response.data;
    if (contentType.includes('application/json')) {
      let payload = null;
      try {
        payload = typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch (parseError) {
        payload = { err: String(raw || '') };
      }
      const message = formatSocialPictureErrorPayload(payload, null, 'ZIP render failed');
      const err = new Error(message);
      err.socialPayload = payload && typeof payload === 'object' ? payload : null;
      throw err;
    }
    if (contentType.includes('application/zip') || contentType.includes('application/octet-stream')) {
      // Unexpected binary zip served as text — treat as base64 if printable, else fail.
    }
    const bytes = decodeBase64ToUint8Array(raw);
    if (!bytes.length) throw new Error('Empty ZIP response');
    return new Blob([bytes], { type: 'application/zip' });
  } catch (error) {
    if (error?.socialPayload) throw error;
    const message = getApiErrorMessage(error, error?.message || 'ZIP download failed');
    const next = new Error(message);
    next.response = error?.response;
    next.socialPayload = getApiErrorPayload(error);
    throw next;
  }
};

export const fetchSocialPicturePngBlob = async (dispatch, form) => {
  if (form?.pictureType === PICTURE_TYPE_MATCH_COVER_GOAL) {
    return fetchMatchCoverGoalZipBlob(dispatch, form);
  }
  const tokens = await ensureAuth(dispatch);
  const pictureType = form.pictureType || PICTURE_TYPE_TABLE;
  const endpoint = resolvePictureEndpoint(pictureType, form.typesCatalog);
  const body = buildSocialPictureBody({ ...form, pictureType, isHtml: false });
  try {
    const response = await axios.post(BASE_URL.url + endpoint, body, {
      headers: authHeaders(tokens.access_token),
      responseType: 'blob',
      timeout: 120000,
    });
    const blob = response.data;
    if (!(blob instanceof Blob) || !blob.size) {
      throw new Error('Empty PNG response');
    }
    const contentType = String(response.headers?.['content-type'] || '');
    if (contentType.includes('application/json') || contentType.includes('text/')) {
      const text = await blob.text();
      let payload = null;
      try {
        payload = JSON.parse(text);
      } catch (parseError) {
        payload = { err: text };
      }
      const message = formatSocialPictureErrorPayload(payload, null, 'PNG render failed');
      const err = new Error(message);
      err.socialPayload = payload && typeof payload === 'object' ? payload : null;
      throw err;
    }
    return blob;
  } catch (error) {
    if (error?.socialPayload) throw error;
    const parsed = await parseBlobError(error, error?.message || 'PNG download failed');
    const next = new Error(parsed.message);
    next.response = error?.response;
    next.socialPayload = parsed.payload;
    throw next;
  }
};

/** @deprecated use fetchSocialPictureHtml */
export const fetchTournamentTableHtml = async (dispatch, form) => {
  const result = await fetchSocialPictureHtml(dispatch, {
    ...form,
    pictureType: form.pictureType || PICTURE_TYPE_TABLE,
  });
  return result.html;
};

/** @deprecated use fetchSocialPicturePngBlob */
export const fetchTournamentTablePngBlob = async (dispatch, form) => (
  fetchSocialPicturePngBlob(dispatch, { ...form, pictureType: form.pictureType || PICTURE_TYPE_TABLE })
);

const DOWNLOAD_PREFIX_BY_PICTURE_TYPE = {
  [PICTURE_TYPE_TOP10]: 'top10',
  [PICTURE_TYPE_CARDS]: 'cards',
  [PICTURE_TYPE_DISQUALIFICATIONS]: 'disqualifications',
  [PICTURE_TYPE_SCHEDULE]: 'schedule',
  [PICTURE_TYPE_RESULTS]: 'results',
  [PICTURE_TYPE_CHESS]: 'chess',
  [PICTURE_TYPE_GROUP_TABLES]: 'group_tables',
  [PICTURE_TYPE_CALENDAR]: 'calendar',
  [PICTURE_TYPE_PLAYOFF]: 'playoff',
  [PICTURE_TYPE_AFISHA]: 'afisha',
  [PICTURE_TYPE_PREVIEW]: 'preview',
  [PICTURE_TYPE_DETAILED_RESULT]: 'detailed_result',
  [PICTURE_TYPE_MATCH_STATS]: 'match_stats',
  [PICTURE_TYPE_ANNOUNCEMENT]: 'announcement',
  [PICTURE_TYPE_QUOTE]: 'quote',
  [PICTURE_TYPE_ACHIEVEMENTS]: 'achievements',
  [PICTURE_TYPE_MVP]: 'mvp',
  [PICTURE_TYPE_MATCH_SUBS]: 'match_subs',
  [PICTURE_TYPE_MATCH_LINEUPS]: 'match_lineups',
  [PICTURE_TYPE_TOUR_TEAM]: 'tour_team',
  [PICTURE_TYPE_MATCH_COVER_GOAL]: 'plashki',
};

export const downloadTournamentTablePng = async (dispatch, form, filename) => {
  const blob = await fetchSocialPicturePngBlob(dispatch, form);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const prefix = DOWNLOAD_PREFIX_BY_PICTURE_TYPE[form.pictureType] || 'tournament_table';
  link.download = filename || `${prefix}_${form.leagueId}_${form.seasonId}.png`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return blob;
};

const extractSponsorsList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.sponsors)) return payload.sponsors;
  if (Array.isArray(payload?.answer)) return payload.answer;
  if (Array.isArray(payload?.answer?.sponsors)) return payload.answer.sponsors;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export const emptySponsorsCache = () => ({
  picture_type: 'tournament_table_v2',
  league_id: null,
  championat_id: null,
  league: null,
  leagueSponsors: null,
  champSchemes: [],
  pool: [],
});

export const extractSponsorsCache = (payload) => {
  const root = payload && typeof payload === 'object' ? payload : {};
  const data = (root.answer && typeof root.answer === 'object' && !Array.isArray(root.answer))
    ? root.answer
    : root;
  const poolRaw = Array.isArray(data.pool) ? data.pool : extractSponsorsList(data.pool);
  return {
    picture_type: data.picture_type || 'tournament_table_v2',
    league_id: data.league_id || data.leagueId || null,
    championat_id: data.championat_id
      || data.league?.championat_id
      || data.championatId
      || null,
    league: data.league || null,
    leagueSponsors: data.leagueSponsors || data.league_sponsors || null,
    champSchemes: Array.isArray(data.champSchemes)
      ? data.champSchemes
      : (Array.isArray(data.champ_schemes) ? data.champ_schemes : []),
    pool: poolRaw,
  };
};

export const fetchSocialSponsorsPool = async (dispatch) => {
  const tokens = await ensureAuth(dispatch);
  try {
    const response = await axios.get(BASE_URL.url + API_URLS.socialSponsors, {
      headers: authHeaders(tokens.access_token, null),
    });
    return extractSponsorsList(response.data);
  } catch (getError) {
    const data = await postJson(dispatch, API_URLS.socialSponsors, {});
    return extractSponsorsList(data);
  }
};

export const addSocialSponsor = async (dispatch, { name, key, imageData, image }) => {
  const body = { name: String(name || '').trim() };
  if (key) body.key = String(key).trim();
  if (imageData) {
    body.imageData = imageData;
    body.dataUrl = imageData;
  }
  if (image) body.image = image;
  const data = await postJson(dispatch, API_URLS.socialSponsorsAdd, body);
  return data?.answer || data?.sponsor || data;
};

export const deleteSocialSponsor = async (dispatch, { id, key }) => {
  const body = {};
  if (id != null && id !== '') body.id = id;
  if (key) body.key = key;
  return postJson(dispatch, API_URLS.socialSponsorsDelete, body);
};

export const fetchTournamentTableSponsorsCache = async (dispatch, { leagueId, pictureType }) => {
  const body = { leagueId: Number(leagueId) };
  if (pictureType) body.picture_type = pictureType;
  const data = await postJson(dispatch, API_URLS.socialTournamentTableSponsors, body);
  return extractSponsorsCache(data);
};

export const saveTournamentTableLeagueSponsors = async (dispatch, { leagueId, sponsors, pictureType }) => {
  const body = {
    leagueId: Number(leagueId),
    sponsors,
  };
  if (pictureType) body.picture_type = pictureType;
  return postJson(dispatch, API_URLS.socialTournamentTableSponsorsSaveLeague, body);
};

export const saveTournamentTableSponsorScheme = async (dispatch, {
  championatId,
  leagueId,
  name,
  sponsors,
  id = null,
}) => {
  const body = {
    name: String(name || '').trim(),
    sponsors,
  };
  if (id) body.id = Number(id);
  if (championatId) body.championat_id = Number(championatId);
  if (leagueId) body.leagueId = Number(leagueId);
  return postJson(dispatch, API_URLS.socialTournamentTableSponsorsSchemesSave, body);
};

export const deleteTournamentTableSponsorScheme = async (dispatch, { id, championatId, leagueId }) => {
  const body = { id: Number(id) };
  if (championatId) body.championat_id = Number(championatId);
  if (leagueId) body.leagueId = Number(leagueId);
  return postJson(dispatch, API_URLS.socialTournamentTableSponsorsSchemesDelete, body);
};
